import ExcelJS from 'exceljs';
import axios from "axios";
import JSZip from "jszip"; // ✅ ADDED

/**
 * MAIN EXPORT FUNCTION
 */
export const exportStockFinancials = async ({
  data,
  groupName,
  filters = {}
}) => {

  const dateStamp = new Date().toISOString().slice(0, 10);
  const timestamp = new Date().toLocaleString();

  const zip = new JSZip(); // ✅ ADDED

  // ================================
  // HELPERS
  // ================================
  const autoSizeColumns = (worksheet) => {
    worksheet.columns.forEach(column => {
      let maxLength = 10;
      column.eachCell({ includeEmpty: true }, cell => {
        const val = cell.value ? cell.value.toString() : "";
        maxLength = Math.max(maxLength, val.length);
      });
      column.width = Math.min(maxLength + 2, 40);
    });
  };

  const boldRow = (row) => {
    row.eachCell(cell => {
      cell.font = { bold: true };
    });
  };

  const formatFilterValue = (key, value) => {
    if (value === undefined || value === null || value === "") {
      if (key === "searchQuery") return "None";
      if (key === "priceThreshold") return "0";
      if (key === "splitPackOnly" || key === "zeroPriceOnly") return "No";
      return "All";
    }

    if (typeof value === "boolean") {
      return value ? "Yes" : "No";
    }

    return value;
  };

  const getFilterLines = () => {
    return [
      `Pharmacy: ${formatFilterValue("selectedPharmacy", filters.selectedPharmacy)}`,
      `Location: ${formatFilterValue("selectedLocation", filters.selectedLocation)}`,
      `Status: ${formatFilterValue("selectedStatus", filters.selectedStatus)}`,
      `Search: ${formatFilterValue("searchQuery", filters.searchQuery)}`,
      `Min Value (£): ${formatFilterValue("priceThreshold", filters.priceThreshold)}`,
      `Expires ≤ (wks): ${filters.expiringWithin !== undefined && filters.expiringWithin !== null && filters.expiringWithin !== "" ? filters.expiringWithin : "Any"}`,
      `On Shelf ≥ (wks): ${filters.onShelfMoreThan !== undefined && filters.onShelfMoreThan !== null && filters.onShelfMoreThan !== "" ? filters.onShelfMoreThan : "Any"}`,
      `Split pack only: ${formatFilterValue("splitPackOnly", filters.splitPackOnly)}`,
      `Zero price only: ${formatFilterValue("zeroPriceOnly", filters.zeroPriceOnly)}`
    ];
  };

  const addFiltersBlock = (sheet) => {
    const startCol = 9;
    const filterLines = getFilterLines();

    sheet.getCell(1, startCol).value = "Filters:";
    sheet.getCell(1, startCol).font = { bold: true };

    let r = 2;
    filterLines.forEach(line => {
      sheet.getCell(r, startCol).value = line;
      r++;
    });
  };

  const downloadWorkbook = async (workbook, filename) => {
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
    });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  // ================================
  // SUMMARY FILE
  // ================================
  const pharmacyMap = {};

  data.forEach(item => {
    const pharmacy = item.pharmacyName || "Unknown";

    if (!pharmacyMap[pharmacy]) {
      pharmacyMap[pharmacy] = { packets: 0, value: 0 };
    }

    pharmacyMap[pharmacy].packets += parseInt(item.balance || 0);
    pharmacyMap[pharmacy].value += item.price || 0;
  });

  const summaryWorkbook = new ExcelJS.Workbook();
  const summarySheet = summaryWorkbook.addWorksheet("Summary");

  summarySheet.addRow([groupName || ""]);
  summarySheet.addRow([`Generated: ${timestamp}`]);
  summarySheet.addRow(["Filters applied:"]);
  getFilterLines().forEach(line => summarySheet.addRow([line]));
  summarySheet.addRow([]);
  summarySheet.addRow(["Pharmacy", "Total Packets", "Total Value (£)"]);

  let grandPackets = 0;
  let grandValue = 0;

  Object.entries(pharmacyMap)
    .sort(([a], [b]) => a.localeCompare(b))
    .forEach(([pharmacy, totals]) => {
      summarySheet.addRow([
        pharmacy,
        totals.packets,
        (totals.value / 100).toFixed(2)
      ]);

      grandPackets += totals.packets;
      grandValue += totals.value;
    });

  summarySheet.addRow([]);
  summarySheet.addRow([
    "GRAND TOTAL",
    grandPackets,
    (grandValue / 100).toFixed(2)
  ]);

  boldRow(summarySheet.getRow(1));
  boldRow(summarySheet.getRow(3));
  boldRow(summarySheet.getRow(6 + getFilterLines().length));
  addFiltersBlock(summarySheet);
  autoSizeColumns(summarySheet);
  summarySheet.views = [{ state: 'frozen', ySplit: 6 + getFilterLines().length }];

  const summaryBuffer = await summaryWorkbook.xlsx.writeBuffer();
  zip.file(`Summary_${dateStamp}.xlsx`, summaryBuffer);

  // ================================
  // PHARMACY FILES
  // ================================
  const pharmacies = [...new Set(data.map(d => d.pharmacyName))];

  for (const pharmacy of pharmacies) {

    const workbook = new ExcelJS.Workbook();

    // ✅ UNIQUE NAME TRACKER PER WORKBOOK
    const usedSheetNames = new Set();

    const getUniqueSheetName = (baseName) => {
      let name = sanitizeSheetName(baseName).substring(0, 31);

      if (!usedSheetNames.has(name)) {
        usedSheetNames.add(name);
        return name;
      }

      let counter = 1;
      let newName;

      do {
        const suffix = `_${counter}`;
        const maxLength = 31 - suffix.length;
        newName = name.substring(0, maxLength) + suffix;
        counter++;
      } while (usedSheetNames.has(newName));

      usedSheetNames.add(newName);
      return newName;
    };

    const pharmacyData = data
      .filter(d => d.pharmacyName === pharmacy)
      .sort((a, b) =>
        (a.drugName || a.vmppNm || "").localeCompare(b.drugName || b.vmppNm || "")
      );

    const totalPackets = pharmacyData.reduce((sum, i) => sum + parseInt(i.balance || 0), 0);
    const totalValue = pharmacyData.reduce((sum, i) => sum + (i.price || 0), 0);

    // ✅ FIXED: unique main sheet
    const sheet = workbook.addWorksheet(getUniqueSheetName(pharmacy));

    sheet.addRow([groupName || ""]);
    sheet.addRow([`Generated: ${timestamp}`]);
    sheet.addRow([`Pharmacy: ${pharmacy}`]);
    sheet.addRow(["Filters applied:"]);
    getFilterLines().forEach(line => sheet.addRow([line]));
    sheet.addRow([]);
    sheet.addRow([`Total stock value: £${(totalValue / 100).toFixed(2)}`]);
    sheet.addRow([`Total number of items: ${totalPackets}`]);
    sheet.addRow([]);
    sheet.addRow([]);
    sheet.addRow(["Drug", "Packets", "Qty", "Location", "First Scanned", "Last Scanned", "Value (£)"]);

    pharmacyData.forEach(item => {
      sheet.addRow([
        item.drugName || item.vmppNm,
        parseInt(item.balance || 0),
        item.qtval,
        item.location,
        formatDate(item.firstScanned),
        formatDate(item.lastScanned),
        ((item.price || 0) / 100).toFixed(2)
      ]);
    });

    boldRow(sheet.getRow(1));
    boldRow(sheet.getRow(4));
    boldRow(sheet.getRow(11 + getFilterLines().length));
    addFiltersBlock(sheet);
    autoSizeColumns(sheet);
    sheet.views = [{ state: 'frozen', ySplit: 11 + getFilterLines().length }];

    const locations = [...new Set(pharmacyData.map(d => d.location))];

    for (const location of locations) {

      // ✅ FIXED: location only + uniqueness
      const locSheet = workbook.addWorksheet(
        getUniqueSheetName(location)
      );

      const locationData = pharmacyData.filter(d => d.location === location);

      const locationPackets = locationData.reduce(
        (sum, i) => sum + parseInt(i.balance || 0),
        0
      );

      const locationValue = locationData.reduce(
        (sum, i) => sum + (i.price || 0),
        0
      );

      let lastPhotoBase64 = null;
      let lastPhotoDate = "";

      try {
        const token = localStorage.getItem("authToken");

        const response = await fetch(
          "https://api.pharmacegroup.co.uk/getShelfPhoto",
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`
            },
            body: JSON.stringify({
              shelfName: locationData[0]?.location,
              pharmacyId: locationData[0]?.pharmacyId
            })
          }
        );

        const doc = await response.json();

        if (doc) {
          lastPhotoBase64 = doc.image;
          lastPhotoDate = doc.createdAt;
        }

      } catch (e) {
        console.error("Photo fetch failed", e);
      }

      locSheet.addRow([groupName || ""]);
      locSheet.addRow([`Generated: ${timestamp}`]);
      locSheet.addRow([`Pharmacy: ${pharmacy}`]);
      locSheet.addRow([`Location: ${location}`]);
      locSheet.addRow(["Filters applied:"]);
      getFilterLines().forEach(line => locSheet.addRow([line]));
      locSheet.addRow([`Last scanned: ${formatDate(locationData[0]?.lastScanned)}`]);
      locSheet.addRow([`Photo date: ${formatDate(lastPhotoDate)}`]);
      locSheet.addRow([]);
      locSheet.addRow([`Total items: ${locationPackets}`]);
      locSheet.addRow([`Total value: £${(locationValue / 100).toFixed(2)}`]);
      locSheet.addRow([]);

      locSheet.addRow(["Drug", "Packets", "Qty", "First Scanned", "Last Scanned", "Value (£)"]);

      locationData.forEach(item => {
        locSheet.addRow([
          item.drugName || item.vmppNm,
          parseInt(item.balance || 0),
          item.qtval,
          formatDate(item.firstScanned),
          formatDate(item.lastScanned),
          ((item.price || 0) / 100).toFixed(2)
        ]);
      });

      boldRow(locSheet.getRow(1));
      boldRow(locSheet.getRow(5));
      boldRow(locSheet.getRow(13 + getFilterLines().length));
      addFiltersBlock(locSheet);
      autoSizeColumns(locSheet);
      locSheet.views = [{ state: 'frozen', ySplit: 13 + getFilterLines().length }];

      if (lastPhotoBase64) {
        const imageId = workbook.addImage({
          base64: lastPhotoBase64,
          extension: 'jpeg',
        });

        locSheet.addImage(imageId, {
          tl: { col: 8, row: 0 },
          ext: { width: 300, height: 200 }
        });
      }
    }

    const buffer = await workbook.xlsx.writeBuffer();
    zip.file(`${sanitizeSheetName(pharmacy)}_${dateStamp}.xlsx`, buffer);
  }

  // ================================
  // FINAL ZIP DOWNLOAD
  // ================================
  const zipBlob = await zip.generateAsync({ type: "blob" });

  const url = window.URL.createObjectURL(zipBlob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `StockFinancials_${dateStamp}.zip`;
  a.click();
  window.URL.revokeObjectURL(url);
};


// ================================
// HELPERS
// ================================
function formatDate(date) {
  if (!date) return "";
  return new Date(date).toLocaleDateString("en-GB");
}

function sanitizeSheetName(name) {
  return name.replace(/[\\/*?:[\]]/g, "").substring(0, 31);
}